import * as React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { IRcaState, LoadTestErrorReason } from "../store/IRcaState";
import {
  DANE_VALIDATION_TEST_RESULT_FORM,
  DEFAULT_TEST_RESULT_FORM,
  IRcaTestResult,
  MTA_STS_VALIDATION_TEST_RESULT_FORM,
  RcaTestResultDisplayFormType,
} from "../models/IRcaTestResult";
import * as actions from "../actions/testResultActions";
import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { TestResultNode, iconNames, getIconColors } from "./TestResultNode";
import { TestResultInsights } from "./TestResultInsight";
import {
  Stack,
  Text,
  Icon,
  CommandBar,
  ICommandBarItemProps,
  ProgressIndicator,
  MessageBar,
  Link,
  MessageBarType,
  Shimmer,
  mergeStyles,
  Separator,
  Depths,
  Announced,
  format,
  useTheme,
} from "@fluentui/react";
import { Utils } from "../Utils";
import DaneValidationResultPage from "./DaneValidationResultPage/DaneValidationResultPage";
import MtaStsValidationResultPage from "./MtaStsValidationResultPage/MtaStsValidationResultPage";
import { RcaThunkDispatch } from "../actions/actionTypes";

const screenReaderOnlyStyle: React.CSSProperties = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
};

const interactiveElementSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[role='button']",
  "[role='link']",
  "[role='textbox']",
  "[role='combobox']",
  "[role='menuitem']",
  "[role='tab']",
  "[role='treeitem']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Checks whether an element is user-interactive and likely has intentional focus.
 *
 * @param element - The element to inspect.
 * @returns A value indicating whether the element is interactive.
 */
const isInteractiveElement = (element: HTMLElement): boolean => element.matches(interactiveElementSelector);

/**
 * The route parameters that are provided by react-router
 */
interface IRouteParams {
  /**
   * The test result id
   */
  resultId: string;
}

/**
 * The props for the TestResultPage component
 */
export interface ITestResultPageProps extends ITestResultPageState, ITestResultPageDispatch {}

/**
 * The props for the TestResultWrapper component and it's children
 */
export interface ITestResultWrapper {
  /**
   * The current result
   */
  currentResult: IRcaTestResult;

  /**
   * The XML download URL
   */
  downloadXmlUrl: string;

  /**
   * The HTML download URL
   */
  downloadHtmlUrl: string;

  /**
   * A delegate to set a result node as expanded/collapsed
   */
  setTestNodeExpanded: (result: IRcaTestResult, expanded: boolean) => void;

  /**
   * A delegate to set all result nodes as expanded/collapsed
   */
  setTestNodeExpandCollapseAll: (expanded: boolean) => void;
}

/**
 * The state props for the component
 */
interface ITestResultPageState {
  /**
   * The user language
   */
  language: string;

  /**
   * The test result id
   */
  resultId: string;

  /**
   * A value indicating whether the load is in progress
   */
  loadInProgress: boolean;

  /**
   * The reason that the result couldn't be loaded
   */
  resultLoadErrorReason: LoadTestErrorReason;

  /**
   * The current result
   */
  currentResult: IRcaTestResult | null;

  /**
   * The progress message
   */
  progressMessage: string | null;

  /**
   * The result XML download URL
   */
  downloadXmlUrl: string;

  /**
   * The result HTML download URL
   */
  downloadHtmlUrl: string;
}

/**
 * The dispatch props for the component
 */
interface ITestResultPageDispatch {
  /**
   * Delegate to trigger the load of the test result
   */
  loadTestResult: (resultId: string, language: string) => void;

  /**
   * Delegate to expand a specific test result node
   */
  setTestNodeExpanded: (result: IRcaTestResult, expanded: boolean) => void;

  /**
   * Delegate to expand or collapse all test result nodes
   */
  setTestNodeExpandCollapseAll: (expanded: boolean) => void;
}

/**
 * Based on RcaTestResultDisplayFormType return a component
 * to use to render results
 *
 * @param formType TestResultDisplayFormType of the test result
 * @returns Component to Render the Test Result
 */
const getTestResultComponentToRender = (formType: RcaTestResultDisplayFormType) => {
  const mapping = {
    null: TestResultTable, // to cover possible corner cases
    [DEFAULT_TEST_RESULT_FORM]: TestResultTable,
    [DANE_VALIDATION_TEST_RESULT_FORM]: DaneValidationResultPage,
    [MTA_STS_VALIDATION_TEST_RESULT_FORM]: MtaStsValidationResultPage,
  };

  return mapping[formType];
};

/**
 * The in progress component
 *
 * @param state - The component state
 * @returns the markup
 */
const InProgressComponent: React.FC<{
  /**
   * progress message
   */
  progressMessage: string;
}> = ({ progressMessage }) => {
  const theme = useTheme();
  const { semanticColors } = theme;
  const loadingStatusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadingStatusRef.current?.focus();
  }, []);

  return (
    <div
      ref={loadingStatusRef}
      tabIndex={-1}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={`${WebsiteStrings.Default_statusTable_Loading} ${progressMessage}`}
    >
      <Stack
        styles={{
          root: {
            margin: "10% auto auto auto",
            width: "70%",
            selectors: {
              [Utils.mobileQuery]: { width: "calc(100% - 20px)" },
            },
          },
        }}
      >
        <Stack
          styles={{
            root: {
              padding: 40,
              backgroundColor: `var(--rca-card-background, ${semanticColors.bodyBackground})`,
              border: "1px solid var(--rca-card-border, transparent)",
              boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
              selectors: { [Utils.mobileQuery]: { padding: 20 } },
            },
          }}
        >
          <Announced message={`${WebsiteStrings.Default_statusTable_Loading} ${progressMessage}`} />
          <ProgressIndicator label={WebsiteStrings.Default_statusTable_Loading} description={progressMessage} />
        </Stack>
      </Stack>
    </div>
  );
};

/**
 * The component rendered when we have an error loading the test
 *
 * @param props - the component props
 * @returns The markup
 */
const TestErrorComponent: React.FC<{
  /**
   * The result load error
   */
  resultLoadErrorReason: LoadTestErrorReason;
}> = ({ resultLoadErrorReason }) => {
  /**
   * Renders the correct markup for the error
   *
   * @returns The markup
   */
  const errorMarkup = (): React.ReactElement => {
    const history = useHistory();

    switch (resultLoadErrorReason) {
      case "NotFound":
        return (
          <MessageBar>
            {WebsiteStrings.Default_cs_TestExpired + " "}
            <Link underline={true} onClick={(): void => history.replace("/")}>
              {ClientStrings.TryAgain}
            </Link>
          </MessageBar>
        );

      case "FailedHttp":
        return (
          <MessageBar messageBarType={MessageBarType.error}>
            {ClientStrings.LoadTestResultHttpError + " "}
            <Link underline={true} onClick={(): void => history.replace("/")}>
              {ClientStrings.TryAgain}
            </Link>
          </MessageBar>
        );
    }

    return <></>;
  };

  return <Stack>{errorMarkup()}</Stack>;
};

/**
 * Component to show the rerun prompt if applicable.
 *
 * @param status - The test status.
 * @returns - The ReactElement with rerun prompt.
 */
const RerunPrompt: React.FC<{
  /**
   * The test status
   */
  status: string;
}> = ({ status }) => {
  switch (status) {
    case "Invalid":
    case "Error":
    case "FatalError":
      return <div style={{ marginTop: 16, fontWeight: "bold" }}>{ClientStrings.RerunPrompt}</div>;

    case "Warning":
    case "SuccessWithWarnings":
      return <div style={{ marginTop: 16, fontWeight: "bold" }}>{ClientStrings.RerunPromptWarning}</div>;

    default:
      return <></>;
  }
};

/**
 * The test result table component
 *
 * @param props - The component props
 * @returns The markup
 */
const TestResultTable: React.FC<ITestResultWrapper> = ({
  currentResult,
  downloadXmlUrl,
  downloadHtmlUrl,
  setTestNodeExpanded,
  setTestNodeExpandCollapseAll,
}) => {
  /**
   * The text to display for the overall result
   */
  const overallResultMessage: { [status: string]: string } = {
    Success: WebsiteStrings.OverallSuccessText,
    SuccessWithWarnings: WebsiteStrings.OverallSuccessWarnText,
    Error: WebsiteStrings.OverallFailText,
    FatalError: WebsiteStrings.OverallFailText,
  };

  // This view is not very mobile friendly, so allow them to switch to
  // a desktop view if needed. Long term, we need a mobile-specific view.
  // The tree just has too much horizontal real-estate for portrait phones
  const [forceDesktop, setForceDesktop] = useState(false);

  useEffect(() => {
    forceDesktop ? Utils.setDesktop() : Utils.setDefaultViewport();
  }, [forceDesktop]);

  const theme = useTheme();
  const iconColors = getIconColors(theme);

  const commandItems: ICommandBarItemProps[] = [
    {
      key: "expand",
      text: currentResult.expanded ? WebsiteStrings.TestResultTable_CollapseAll : WebsiteStrings.TestResultTable_ExpandAll,
      ariaLabel: currentResult.expanded ? WebsiteStrings.TestResultTable_CollapseAll : WebsiteStrings.TestResultTable_ExpandAll,
      iconProps: {
        iconName: currentResult.expanded ? "DoubleChevronUp" : "DoubleChevronDown",
      },
      onClick: (): void => setTestNodeExpandCollapseAll(!currentResult.expanded),
    },
    {
      key: "saveAsXml",
      title: WebsiteStrings.TestResultTable_SaveAsXml,
      ariaLabel: WebsiteStrings.TestResultTable_SaveAsXml,
      iconOnly: true,
      iconProps: {
        iconName: "ExternalXAML",
      },
      onClick: (): void => {
        window.location.href = downloadXmlUrl;
      },
    },
    {
      key: "saveAsHtml",
      title: WebsiteStrings.TestResultTable_SaveAsHtml,
      ariaLabel: WebsiteStrings.TestResultTable_SaveAsHtml,
      iconOnly: true,
      iconProps: {
        iconName: "FileHTML",
      },
      onClick: (): void => {
        window.location.href = downloadHtmlUrl;
      },
    },
  ];

  const testDetailsStyle = mergeStyles(theme.fonts.large, {
    selectors: { [Utils.mobileQuery]: theme.fonts.medium },
  });

  const noMarginStyle = mergeStyles({ margin: 0 });
  const testDetailsHeadingId = "test-details-heading";
  const cardSurfaceStyles = {
    backgroundColor: `var(--rca-card-background, ${theme.semanticColors.bodyBackground})`,
    border: "1px solid var(--rca-card-border, transparent)",
    borderRadius: 2,
    boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
  };
  const resultBannerButtonShadow = "0 2px 6px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)";
  const resultBannerButtonShadowHover = "0 4px 10px rgba(0, 0, 0, 0.14), 0 2px 4px rgba(0, 0, 0, 0.08)";

  return (
    <Stack
      as="main"
      role="main"
      aria-label={WebsiteStrings.TestResultTable_TestDetails}
      styles={{
        root: {
          width: "100%",
          paddingLeft: 20,
          paddingRight: 20,
          backgroundColor: theme.semanticColors.bodyBackground,
          selectors: { [Utils.mobileQuery]: { paddingLeft: 10, paddingRight: 10 } },
        },
      }}
    >
      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 8 }}
        styles={{
          root: {
            ...cardSurfaceStyles,
            marginTop: 16,
            marginBottom: 16,
            padding: 16,
            selectors: {
              [Utils.mobileQuery]: {
                alignItems: "flex-start",
                flexWrap: "wrap",
              },
            },
          },
        }}
      >
        <Icon
          iconName={iconNames[currentResult.Status]}
          aria-hidden="true"
          styles={{
            root: { fontSize: theme.fonts.xLargePlus.fontSize, color: iconColors[currentResult.Status], paddingRight: 5, paddingLeft: 5 },
          }}
        />
        <h2 className={noMarginStyle}>
          <Text
            styles={{
              root: mergeStyles(theme.fonts.xLargePlus, {
                selectors: { [Utils.mobileQuery]: theme.fonts.mediumPlus },
              }),
            }}
          >
            {overallResultMessage[currentResult.Status]}
          </Text>
        </h2>
        <Stack grow={2} horizontalAlign="end">
          {forceDesktop ? (
            <Link underline={true} onClick={(): void => setForceDesktop(false)}>
              {ClientStrings.SwitchToMobile}
            </Link>
          ) : (
            <Link
              underline={true}
              styles={{ root: { display: "none", selectors: { [Utils.mobileQuery]: { display: "unset" } } } }}
              onClick={(): void => setForceDesktop(true)}
            >
              {ClientStrings.SwitchToDesktop}
            </Link>
          )}
        </Stack>
      </Stack>
      <Stack
        as="section"
        role="region"
        aria-labelledby={testDetailsHeadingId}
        aria-label={WebsiteStrings.TestResultTable_TestDetails}
        styles={{
          root: {
            ...cardSurfaceStyles,
            padding: 10,
            paddingTop: 0,
            overflow: "hidden",
          },
        }}
      >
        <Stack
          horizontal
          verticalAlign="center"
          horizontalAlign="space-between"
          styles={{
            root: {
              backgroundColor: `var(--rca-card-background, ${theme.semanticColors.bodyBackground})`,
              borderBottom: "1px solid var(--rca-card-border, transparent)",
              paddingTop: 10,
              paddingBottom: 10,
              paddingLeft: 10,
              paddingRight: 10,
            },
          }}
        >
          <h3 id={testDetailsHeadingId} className={noMarginStyle}>
            <Text styles={{ root: testDetailsStyle }}>{WebsiteStrings.TestResultTable_TestDetails}</Text>
          </h3>
          <CommandBar
            items={commandItems}
            styles={{
              root: {
                margin: 0,
                padding: 0,
                minWidth: 200,
                height: 44,
                backgroundColor: "transparent",
                selectors: {
                  [Utils.mobileQuery]: {
                    height: 35,
                  },
                  ".ms-Button": {
                    backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
                    border: "1px solid var(--rca-control-border, transparent)",
                    boxShadow: resultBannerButtonShadow,
                    color: theme.semanticColors.bodyText,
                    marginLeft: 8,
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                  },
                  ".ms-Button:hover": {
                    backgroundColor: theme.semanticColors.bodyBackgroundHovered,
                    borderColor: "var(--rca-control-border-hover, transparent)",
                    boxShadow: resultBannerButtonShadowHover,
                    color: theme.semanticColors.bodyText,
                  },
                  ".ms-Button:focus-visible": {
                    backgroundColor: theme.semanticColors.bodyBackgroundHovered,
                    borderColor: "var(--rca-control-border-hover, transparent)",
                    boxShadow: resultBannerButtonShadowHover,
                    color: theme.semanticColors.bodyText,
                  },
                  ".ms-Button .ms-Button-icon": {
                    color: theme.semanticColors.bodyText,
                  },
                },
              },
            }}
          />
        </Stack>
        <Separator styles={{ root: { height: 5, padding: 0, margin: 0, marginTop: 0 } }} />
        <Stack
          styles={{
            root: {
              paddingTop: 12,
              lineHeight: 23,
              whiteSpace: "pre-line",
              wordBreak: "normal",
              overflowWrap: "break-word",
              ...theme.fonts.medium,
            },
          }}
        >
          <TestResultInsights currentResult={currentResult} />
          <Stack role="tree" aria-label={WebsiteStrings.TestResultTable_TestDetails}>
            <TestResultNode result={currentResult} setExpanded={setTestNodeExpanded} />
          </Stack>
          <RerunPrompt status={currentResult.Status} />
        </Stack>
      </Stack>
    </Stack>
  );
};

/**
 * Wrapper around Test Result view.
 * It will choose component for render based on TestResultDisplayForm.
 *
 * @param props - The component properties
 * @returns The markup
 */
const TestResultWrapper: React.FC<ITestResultWrapper> = (props) => {
  const ComponentToRender = getTestResultComponentToRender(props.currentResult.DisplayFormType);

  return React.createElement(ComponentToRender, props, null);
};

/**
 * The test result page component
 *
 * @param props - The component properties
 * @returns The markup
 */
export const TestResultPage: React.FC<ITestResultPageProps> = ({
  language,
  resultId,
  loadInProgress,
  loadTestResult,
  currentResult,
  progressMessage,
  resultLoadErrorReason,
  setTestNodeExpanded,
  setTestNodeExpandCollapseAll,
  downloadXmlUrl,
  downloadHtmlUrl,
}) => {
  useEffect(() => loadTestResult(resultId, language), [loadTestResult, resultId, language]);
  const testResultsPageTitle = format(WebsiteStrings.TestResultsPageTitle, WebsiteStrings.ProductTitle);
  const [resultsAnnouncement, setResultsAnnouncement] = useState("");
  const announcedResultIdRef = useRef<string | null>(null);
  const focusedResultIdRef = useRef<string | null>(null);
  const resultPageRegionRef = useRef<HTMLDivElement>(null);
  const loadingRegionRef = useRef<HTMLDivElement>(null);
  const focusedLoadingResultIdRef = useRef<string | null>(null);

  // Update the document title based on loading state to provide proper screen reader context
  useEffect(() => {
    if (loadInProgress) {
      if (document.title !== WebsiteStrings.Default_statusTable_Loading) {
        document.title = WebsiteStrings.Default_statusTable_Loading;
      }
    } else if (currentResult) {
      if (document.title !== testResultsPageTitle) {
        document.title = testResultsPageTitle;
      }
    }
  }, [loadInProgress, currentResult, testResultsPageTitle]);

  useEffect(() => {
    if (!currentResult) {
      announcedResultIdRef.current = null;
      focusedResultIdRef.current = null;
      setResultsAnnouncement("");
      return;
    }

    if (announcedResultIdRef.current === resultId) {
      return;
    }

    announcedResultIdRef.current = resultId;
    setResultsAnnouncement("");

    const announceTimeoutId = window.setTimeout(() => {
      setResultsAnnouncement(testResultsPageTitle);
    }, 100);

    return (): void => window.clearTimeout(announceTimeoutId);
  }, [currentResult, resultId, testResultsPageTitle]);

  useEffect(() => {
    if (!loadInProgress || progressMessage || currentResult || focusedLoadingResultIdRef.current === resultId) {
      return;
    }

    focusedLoadingResultIdRef.current = resultId;
    const focusTimeoutId = window.setTimeout(() => {
      loadingRegionRef.current?.focus();
    }, 0);

    return (): void => window.clearTimeout(focusTimeoutId);
  }, [currentResult, loadInProgress, progressMessage, resultId]);

  useEffect(() => {
    if (!currentResult || focusedResultIdRef.current === resultId) {
      return;
    }

    focusedResultIdRef.current = resultId;
    let firstFrameId: number | undefined;
    let secondFrameId: number | undefined;

    const focusTimeoutId = window.setTimeout(() => {
      firstFrameId = window.requestAnimationFrame(() => {
        secondFrameId = window.requestAnimationFrame(() => {
          const activeElement = document.activeElement;
          const resultsRegion = resultPageRegionRef.current;

          if (activeElement instanceof HTMLElement && activeElement !== document.body) {
            if ((resultsRegion && resultsRegion.contains(activeElement)) || isInteractiveElement(activeElement)) {
              return;
            }
          }

          resultsRegion?.querySelector<HTMLElement>("[role='treeitem']")?.focus();
        });
      });
    }, 600);

    return (): void => {
      window.clearTimeout(focusTimeoutId);

      if (firstFrameId !== undefined) {
        window.cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId !== undefined) {
        window.cancelAnimationFrame(secondFrameId);
      }
    };
  }, [currentResult, resultId]);

  return (
    <Stack verticalFill styles={{ root: { width: "100%" } }}>
      {currentResult ? (
        <>
          <div role="status" aria-live="assertive" aria-atomic="true" style={screenReaderOnlyStyle}>
            {resultsAnnouncement}
          </div>
          <div ref={resultPageRegionRef}>
            <TestResultWrapper
              currentResult={currentResult}
              setTestNodeExpanded={setTestNodeExpanded}
              setTestNodeExpandCollapseAll={setTestNodeExpandCollapseAll}
              downloadXmlUrl={downloadXmlUrl}
              downloadHtmlUrl={downloadHtmlUrl}
            />
          </div>
        </>
      ) : resultLoadErrorReason ? (
        <TestErrorComponent resultLoadErrorReason={resultLoadErrorReason} />
      ) : progressMessage ? (
        <InProgressComponent progressMessage={progressMessage} />
      ) : loadInProgress ? (
        <div
          ref={loadingRegionRef}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={WebsiteStrings.Default_statusTable_Loading}
          tabIndex={-1}
        >
          <Stack verticalFill verticalAlign="center">
            <Announced message={WebsiteStrings.Default_statusTable_Loading} />
            <div style={{ width: "50%", marginLeft: "auto", marginRight: "auto" }}>
              <Shimmer aria-label={WebsiteStrings.Default_statusTable_Loading} />
            </div>
          </Stack>
        </div>
      ) : (
        <></>
      )}
    </Stack>
  );
};

/**
 * Redux connected component MapStateToProps function
 *
 * @param state - The application state
 * @param ownProps - Other properties (from react-router, etc)
 * @returns The state props that will be used by the component
 */
const mapStateToProps = (state: IRcaState, ownProps: RouteComponentProps<IRouteParams>): ITestResultPageState => {
  return {
    resultId: ownProps.match.params.resultId,
    language: state.globalConfig.language,
    loadInProgress: state.testResult.loadInProgress,
    currentResult: state.testResult.currentResult,
    progressMessage: state.testResult.progressMessage,
    resultLoadErrorReason: state.testResult.resultLoadErrorReason,
    downloadXmlUrl: state.testResult.downloadXmlUrl,
    downloadHtmlUrl: state.testResult.downloadHtmlUrl,
  };
};

/**
 * Redux connected component MapDispatchToProps function
 *
 * @param dispatch - The dispatch method
 * @returns The set of dispatch delegates
 */
const mapDispatchToProps = (dispatch: RcaThunkDispatch): ITestResultPageDispatch => {
  return {
    loadTestResult: (resultId: string, language: string): void => {
      void dispatch(actions.loadTestResult(resultId, language));
    },
    setTestNodeExpanded: (result: IRcaTestResult, expanded: boolean): void => {
      dispatch(actions.setTestNodeExpanded(result, expanded));
    },
    setTestNodeExpandCollapseAll: (expanded: boolean): void => {
      dispatch(actions.setTestNodeExpandCollapseAll(expanded));
    },
  };
};

// Normal to export a redux connected component this way
export default connect(mapStateToProps, mapDispatchToProps)(TestResultPage);
